import { Fragment, useMemo, useCallback } from "react";
import { Container } from "@/components/container";
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from "@/partials/toolbar";
import { useLayout } from "@/providers";
import { DataGrid, KeenIcon } from "@/components";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { formatDisplayDateTime } from '@/utils/Date';
import {
  useGetThemeRouteRevisionsQuery,
  useRevertThemeRouteToRevisionMutation,
} from "@/services/ThemeRoutesService";

interface ThemeRouteRevisionCreator {
  id: string;
  name?: string;
  email?: string;
}

interface ThemeRouteRevisionContent {
  name?: string;
  description?: string;
  theme?: string;
  language?: string;
}

interface ThemeRouteRevisionItem {
  id: string;
  theme_route_id?: string;
  version?: number;
  change_summary?: string;
  created_by?: string;
  creator?: ThemeRouteRevisionCreator;
  content?: ThemeRouteRevisionContent;
  // Optional name for backward compatibility with older API shapes
  name?: string;
  // Support both backend field shapes for created date
  created_at?: string;
  createdAt?: string;
}

const ThemeRouteVersions = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: revisionsData,
    isLoading,
    refetch,
  } = useGetThemeRouteRevisionsQuery(id as string, {
    skip: !id,
  });

  const [revertToRevision, { isLoading: isReverting }] =
    useRevertThemeRouteToRevisionMutation();

  const revisions: ThemeRouteRevisionItem[] = useMemo(() => {
    if (!revisionsData || !Array.isArray(revisionsData.data)) return [];
    return revisionsData.data as ThemeRouteRevisionItem[];
  }, [revisionsData]);

  const handleRevertVersion = useCallback(
    async (revision: ThemeRouteRevisionItem) => {
      if (!id) return;

      if (typeof revision.version !== "number") {
        enqueueSnackbar("Selected revision does not have a version number", {
          variant: "error",
        });
        return;
      }

      try {
        await revertToRevision({
          id,
          version: revision.version,
          reason: "Reverting to previous approved version",
        }).unwrap();
        enqueueSnackbar(
          "Version reverted successfully. The route is now in draft; request approval to publish.",
          {
            variant: "success",
          },
        );
        refetch();
      } catch (error) {
         
        console.error(error);
        enqueueSnackbar("Failed to revert version", { variant: "error" });
      }
    },
    [id, revertToRevision, enqueueSnackbar, refetch],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }: { row: { original: ThemeRouteRevisionItem } }) => {
          const name =
            row.original.content?.name ||
            row.original.name ||
            "(Untitled route)";

          return <span className="font-medium text-gray-800">{name}</span>;
        },
      },
      {
        accessorKey: "version",
        header: "Version",
        cell: ({ row }: { row: { original: ThemeRouteRevisionItem } }) => (
          <span className="badge badge-sm badge-primary">
            {typeof row.original.version === "number"
              ? `v${row.original.version}`
              : "-"}
          </span>
        ),
        meta: { className: "w-24" },
      },
      {
        accessorKey: "change_summary",
        header: "Change Summary",
        cell: ({ row }: { row: { original: ThemeRouteRevisionItem } }) => (
          <span className="text-gray-700">
            {row.original.change_summary || "-"}
          </span>
        ),
      },
      {
        accessorKey: "creator",
        header: "Created By",
        cell: ({ row }: { row: { original: ThemeRouteRevisionItem } }) => {
          const creator = row.original.creator;
          const label =
            creator?.name || creator?.email || row.original.created_by || "-";
          return <span className="text-gray-700">{label}</span>;
        },
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        cell: ({ row }: { row: { original: ThemeRouteRevisionItem } }) => {
          const raw = row.original.created_at || row.original.createdAt;
          const label = raw ? formatDisplayDateTime(new Date(raw)) : "-";
          return <span className="text-gray-600">{label}</span>;
        },
      },
      {
        id: "revert",
        header: "Revert",
        cell: ({ row }: { row: { original: ThemeRouteRevisionItem } }) => (
          <button
            className="btn btn-sm btn-light btn-outline"
            type="button"
            onClick={() => handleRevertVersion(row.original)}
            disabled={isReverting}
          >
            <KeenIcon icon="history" className="mr-1" />
            Revert
          </button>
        ),
        meta: { className: "w-32" },
      },
    ],
    [handleRevertVersion, isReverting],
  );

  if (isLoading && !revisions.length) {
    return <div className="p-10 text-center">Loading versions...</div>;
  }

  return (
    <Fragment>
      {currentLayout?.name === "demo1-layout" && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Theme Route Versions" />
              <ToolbarDescription>
                View and manage version history for this theme route.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                className="btn btn-sm btn-light h-10"
                type="button"
                onClick={() => navigate(`/theme-routes`)}
              >
                <KeenIcon icon="arrow-left" className="mr-1" />
                Back to Theme Routes
              </button>
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="card">
          <div className="card-body">
            <DataGrid
              columns={columns}
              data={revisions}
              rowSelect={false}
              loading={isLoading}
              pagination={{
                size: 10,
              }}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ThemeRouteVersions;
